import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Alert
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { API } from '../../../api/urls';
import { fetchPublicCompanyLogo, fetchPublicCompanyFavicon, fetchCompanyInfoByDomain } from '../../../api/requests/company-requests';

const CompanyWelcomePage = () => {
    const [companyData, setCompanyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logoUrl, setLogoUrl] = useState(null);
    const [faviconUrl, setFaviconUrl] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Hoş Geldiniz | Tahsilat360";
        fetchCompanyData();
        // Animate on mount
        setTimeout(() => setIsVisible(true), 100);
    }, []);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetchCompanyInfoByDomain();

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Şirket bilgileri alınamadı`);
            }

            const result = await response.json();

            if (result.statusCode === 200 && result.data) {
                setCompanyData(result.data);

                if (result.data.companyName) {
                    document.title = `${result.data.companyName} | Tahsilat360`;
                }

                await loadCompanyAssets();

            } else {
                throw new Error(result.message || 'Şirket bilgileri bulunamadı');
            }

        } catch (err) {
            console.error('Company data fetch error:', err);
            setError(err.message || 'Şirket bilgileri yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const loadCompanyAssets = async () => {
        try {
            const logoResponse = await fetchPublicCompanyLogo();

            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                const logoObjectUrl = URL.createObjectURL(logoBlob);
                setLogoUrl(logoObjectUrl);
            }

            const faviconResponse = await fetchPublicCompanyFavicon();

            if (faviconResponse.ok) {
                const faviconBlob = await faviconResponse.blob();
                const faviconObjectUrl = URL.createObjectURL(faviconBlob);
                setFaviconUrl(faviconObjectUrl);
                updateFavicon(faviconObjectUrl);
            }

        } catch (err) {
            console.warn('Company assets could not be loaded:', err);
        }
    };

    const updateFavicon = (faviconUrl) => {
        let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
    };

    const handleLoginClick = () => {
        navigate('/login');
    };

    useEffect(() => {
        return () => {
            if (logoUrl) URL.revokeObjectURL(logoUrl);
            if (faviconUrl) URL.revokeObjectURL(faviconUrl);
        };
    }, [logoUrl, faviconUrl]);

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden"
                 style={{
                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                 }}>
                {/* Animated background elements */}
                <div className="position-absolute w-100 h-100">
                    <div className="position-absolute rounded-circle bg-white opacity-10"
                         style={{
                             width: '300px',
                             height: '300px',
                             top: '10%',
                             left: '-10%',
                             animation: 'float 6s ease-in-out infinite'
                         }}>
                    </div>
                    <div className="position-absolute rounded-circle bg-white opacity-5"
                         style={{
                             width: '200px',
                             height: '200px',
                             bottom: '20%',
                             right: '-5%',
                             animation: 'float 8s ease-in-out infinite reverse'
                         }}>
                    </div>
                </div>

                <Container className="position-relative">
                    <Row className="justify-content-center">
                        <Col md={6} className="text-center">
                            <div className="bg-white rounded-5 shadow-lg p-5 border-0"
                                 style={{
                                     backdropFilter: 'blur(10px)',
                                     background: 'rgba(255, 255, 255, 0.95)'
                                 }}>
                                <div className="mb-4">
                                    <div className="spinner-border text-primary"
                                         style={{ width: '3rem', height: '3rem' }}>
                                    </div>
                                </div>
                                <h5 className="text-dark fw-bold mb-2">Hoş Geldiniz</h5>
                                <p className="text-muted mb-0">Şirket bilgileri yükleniyor...</p>
                            </div>
                        </Col>
                    </Row>
                </Container>

            </div>
        );
    }

    if (error) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                 style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)' }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="shadow-lg border-0 rounded-5 overflow-hidden">
                                <CardBody className="p-5 text-center">
                                    <div className="mb-4">
                                        <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center"
                                             style={{ width: '100px', height: '100px' }}>
                                            <i className="bx bx-error-circle display-4 text-danger"></i>
                                        </div>
                                    </div>
                                    <h3 className="text-danger mb-3 fw-bold">Oops! Bir Sorun Oluştu</h3>
                                    <p className="text-muted mb-4 fs-5">{error}</p>
                                    <Button
                                        color="danger"
                                        size="lg"
                                        className="rounded-pill px-4"
                                        onClick={() => window.location.reload()}
                                        style={{ minWidth: '160px' }}
                                    >
                                        <i className="bx bx-refresh me-2"></i>
                                        Tekrar Dene
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="min-vh-100 position-relative overflow-hidden"
             style={{
                 background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 25%, #ec4899 50%, #f97316 75%, #eab308 100%)',
                 backgroundSize: '400% 400%',
                 animation: 'gradientShift 15s ease infinite'
             }}>

            {/* Animated background patterns */}
            <div className="position-absolute w-100 h-100 overflow-hidden">
                <div className="position-absolute"
                     style={{
                         width: '400px',
                         height: '400px',
                         background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                         top: '-10%',
                         left: '-10%',
                         borderRadius: '50%',
                         animation: 'float 20s linear infinite'
                     }}>
                </div>
                <div className="position-absolute"
                     style={{
                         width: '300px',
                         height: '300px',
                         background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                         bottom: '-10%',
                         right: '-10%',
                         borderRadius: '50%',
                         animation: 'float 25s linear infinite reverse'
                     }}>
                </div>

                {/* Floating geometric shapes */}
                <div className="position-absolute opacity-10">
                    {[...Array(6)].map((_, i) => (
                        <div key={i}
                             className="position-absolute bg-white"
                             style={{
                                 width: Math.random() * 20 + 10 + 'px',
                                 height: Math.random() * 20 + 10 + 'px',
                                 top: Math.random() * 100 + '%',
                                 left: Math.random() * 100 + '%',
                                 borderRadius: Math.random() > 0.5 ? '50%' : '20%',
                                 animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
                                 animationDelay: Math.random() * 5 + 's'
                             }}>
                        </div>
                    ))}
                </div>
            </div>

            <Container fluid className="py-5 position-relative">
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col xl={11} lg={12}>

                        {/* Hero Section */}
                        <div className={`text-center mb-5 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            <div className="bg-white bg-opacity-10 rounded-5 backdrop-blur-sm p-4 d-inline-block mb-4">
                                <h6 className="text-white mb-0 fw-semibold">
                                    <i className="bx bx-shield-check me-2"></i>
                                    Güvenli Ödeme Platformu
                                </h6>
                            </div>
                            <h1 className="display-4 text-white fw-bold mb-3">
                                Hoş Geldiniz
                                <div className="text-warning">Tahsilat360</div>
                            </h1>
                            <p className="text-white-50 fs-5 mb-0 mx-auto" style={{ maxWidth: '600px' }}>
                                Modern, güvenli ve kullanıcı dostu ödeme deneyimi için tasarlandı
                            </p>
                        </div>

                        <Row className="g-4 align-items-stretch">
                            {/* Company Info Card - Enhanced */}
                            <Col lg={8}>
                                <Card className={`h-100 shadow-lg border-0 rounded-5 overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                      style={{
                                          background: 'rgba(255, 255, 255, 0.95)',
                                          backdropFilter: 'blur(20px)',
                                          border: '1px solid rgba(255, 255, 255, 0.2)',
                                          transitionDelay: '200ms'
                                      }}>

                                    {/* Company Header with Gradient */}
                                    <div className="position-relative"
                                         style={{
                                             background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                             height: '140px'
                                         }}>

                                        {/* Animated pattern overlay */}
                                        <div className="position-absolute top-0 start-0 w-100 h-100 opacity-20"
                                             style={{
                                                 backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h40V20H20zM0 0h20v20H0V0z'/%3E%3C/g%3E%3C/svg%3E")`,
                                                 animation: 'patternMove 20s linear infinite'
                                             }}>
                                        </div>

                                        {/* Company logo area */}
                                        <div className="position-absolute bottom-0 start-0 ms-4"
                                             style={{ transform: 'translateY(50%)' }}>
                                            <div className="position-relative">
                                                <div className="bg-white rounded-4 shadow-lg p-3 d-inline-flex align-items-center justify-content-center"
                                                     style={{
                                                         width: '120px',
                                                         height: '120px',
                                                         border: '4px solid rgba(255, 255, 255, 0.9)'
                                                     }}>
                                                    {logoUrl ? (
                                                        <img
                                                            src={logoUrl}
                                                            alt={`${companyData?.companyName || 'Şirket'} Logo`}
                                                            className="img-fluid rounded-3"
                                                            style={{
                                                                maxWidth: '90px',
                                                                maxHeight: '90px',
                                                                objectFit: 'contain',
                                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div className={`d-flex align-items-center justify-content-center w-100 h-100 ${logoUrl ? 'd-none' : ''}`}>
                                                        <i className="bx bx-building display-4 text-primary"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <CardBody className="p-5" style={{ paddingTop: '4rem !important' }}>
                                        <Row>
                                            <Col md={12}>
                                                <div className="mb-5">
                                                    <h1 className="display-6 fw-bold text-dark mb-3 position-relative">
                                                        {companyData?.companyName || companyData?.name || 'Şirket Adı'}
                                                        <div className="position-absolute bottom-0 start-0 bg-primary"
                                                             style={{
                                                                 width: '60px',
                                                                 height: '4px',
                                                                 borderRadius: '2px',
                                                                 marginTop: '8px'
                                                             }}>
                                                        </div>
                                                    </h1>

                                                    {companyData?.description && (
                                                        <p className="text-muted fs-5 mb-0 lh-base" style={{ fontWeight: '400' }}>
                                                            {companyData.description}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Company Details Grid */}
                                                <div className="row g-4">
                                                    {companyData?.address && (
                                                        <div className="col-12">
                                                            <div className="d-flex align-items-start p-3 rounded-4 bg-light bg-opacity-50 hover-shadow-sm transition-all">
                                                                <div className="flex-shrink-0">
                                                                    <div className="bg-primary bg-opacity-15 rounded-3 p-3">
                                                                        <i className="bx bx-map text-primary fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <h6 className="mb-2 fw-bold text-dark">Adres</h6>
                                                                    <p className="text-muted mb-0 lh-base">{companyData.address}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {(companyData?.phoneNumber || companyData?.phone) && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-start p-3 rounded-4 bg-light bg-opacity-50 hover-shadow-sm transition-all h-100">
                                                                <div className="flex-shrink-0">
                                                                    <div className="bg-success bg-opacity-15 rounded-3 p-3">
                                                                        <i className="bx bx-phone text-success fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <h6 className="mb-2 fw-bold text-dark">Telefon</h6>
                                                                    <p className="text-muted mb-0">
                                                                        <a href={`tel:${companyData.phoneNumber || companyData.phone}`}
                                                                           className="text-decoration-none text-success fw-semibold">
                                                                            {companyData.phoneNumber || companyData.phone}
                                                                        </a>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {companyData?.email && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-start p-3 rounded-4 bg-light bg-opacity-50 hover-shadow-sm transition-all h-100">
                                                                <div className="flex-shrink-0">
                                                                    <div className="bg-info bg-opacity-15 rounded-3 p-3">
                                                                        <i className="bx bx-envelope text-info fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <h6 className="mb-2 fw-bold text-dark">E-posta</h6>
                                                                    <p className="text-muted mb-0">
                                                                        <a href={`mailto:${companyData.email}`}
                                                                           className="text-decoration-none text-info fw-semibold">
                                                                            {companyData.email}
                                                                        </a>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {companyData?.website && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-start p-3 rounded-4 bg-light bg-opacity-50 hover-shadow-sm transition-all h-100">
                                                                <div className="flex-shrink-0">
                                                                    <div className="bg-warning bg-opacity-15 rounded-3 p-3">
                                                                        <i className="bx bx-globe text-warning fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <h6 className="mb-2 fw-bold text-dark">Website</h6>
                                                                    <p className="text-muted mb-0">
                                                                        <a href={companyData.website}
                                                                           target="_blank"
                                                                           rel="noopener noreferrer"
                                                                           className="text-decoration-none text-warning fw-semibold">
                                                                            {companyData.website.replace(/^https?:\/\//, '')}
                                                                        </a>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {(companyData?.taxNumber || companyData?.taxNo) && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-start p-3 rounded-4 bg-light bg-opacity-50 hover-shadow-sm transition-all h-100">
                                                                <div className="flex-shrink-0">
                                                                    <div className="bg-secondary bg-opacity-15 rounded-3 p-3">
                                                                        <i className="bx bx-receipt text-secondary fs-4"></i>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <h6 className="mb-2 fw-bold text-dark">Vergi No</h6>
                                                                    <p className="text-muted mb-0 fw-semibold">
                                                                        {companyData.taxNumber || companyData.taxNo}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            {/* Login Card - Enhanced */}
                            <Col lg={4}>
                                <Card className={`h-100 shadow-lg border-0 rounded-5 position-relative overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                      style={{
                                          background: 'rgba(255, 255, 255, 0.95)',
                                          backdropFilter: 'blur(20px)',
                                          border: '1px solid rgba(255, 255, 255, 0.2)',
                                          transitionDelay: '400ms'
                                      }}>

                                    {/* Decorative top gradient */}
                                    <div className="position-absolute top-0 start-0 w-100"
                                         style={{
                                             height: '4px',
                                             background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                         }}>
                                    </div>

                                    <CardBody className="p-5 text-center d-flex flex-column h-100">
                                        <div className="mb-4">
                                            <div className="bg-primary bg-opacity-10 rounded-4 d-inline-flex align-items-center justify-content-center position-relative"
                                                 style={{ width: '100px', height: '100px' }}>
                                                <i className="bx bx-user-circle display-4 text-primary"></i>
                                                <div className="position-absolute top-0 end-0 bg-success rounded-circle"
                                                     style={{ width: '20px', height: '20px', transform: 'translate(25%, -25%)' }}>
                                                </div>
                                            </div>
                                        </div>

                                        <h3 className="fw-bold text-dark mb-3">
                                            Hesabınıza Giriş Yapın
                                        </h3>

                                        <p className="text-muted mb-4 lh-base flex-grow-1">
                                            Güvenli ödeme sistemimize erişin ve tüm premium özelliklerden anında yararlanmaya başlayın.
                                        </p>

                                        <div className="d-grid gap-3 mb-4">
                                            <Button
                                                color="primary"
                                                size="lg"
                                                className="rounded-pill fw-bold position-relative overflow-hidden"
                                                onClick={handleLoginClick}
                                                style={{
                                                    minHeight: '56px',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    border: 'none',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.2)';
                                                }}
                                            >
                                                <i className="bx bx-log-in me-2 fs-5"></i>
                                                Güvenli Giriş
                                            </Button>

                                            <div className="text-center">
                                                <small className="text-muted">
                                                    Henüz hesabınız yok mu?
                                                    <a href="/register" className="text-primary text-decoration-none ms-1 fw-semibold">
                                                        Hemen kayıt olun
                                                    </a>
                                                </small>
                                            </div>
                                        </div>

                                        {/* Enhanced Features List */}
                                        <div className="pt-4 border-top">
                                            <h6 className="text-dark fw-bold mb-3">Platform Avantajları</h6>
                                            <div className="text-start">
                                                {[
                                                    { icon: 'bx-shield-check', text: 'SSL şifreli güvenli ödeme', color: 'success' },
                                                    { icon: 'bx-time-five', text: '7/24 kesintisiz hizmet', color: 'info' },
                                                    { icon: 'bx-line-chart', text: 'Anlık raporlama ve takip', color: 'warning' },
                                                    { icon: 'bx-support', text: 'Öncelikli müşteri desteği', color: 'primary' }
                                                ].map((feature, index) => (
                                                    <div key={index} className="d-flex align-items-center mb-3 p-2 rounded-3 hover-bg-light transition-all">
                                                        <div className={`bg-${feature.color} bg-opacity-15 rounded-circle p-2 me-3 flex-shrink-0`}>
                                                            <i className={`bx ${feature.icon} text-${feature.color} fs-6`}></i>
                                                        </div>
                                                        <small className="text-muted fw-medium lh-base">
                                                            {feature.text}
                                                        </small>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {/* Enhanced Footer */}
                        <Row className="mt-5">
                            <Col xs={12}>
                                <div className={`bg-white bg-opacity-10 rounded-5 p-4 backdrop-blur-sm border border-white border-opacity-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                     style={{ transitionDelay: '600ms' }}>
                                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between">
                                        <div className="text-white mb-3 mb-md-0 text-center text-md-start">
                                            <h5 className="mb-1 fw-bold">
                                                <i className="bx bx-credit-card me-2"></i>
                                                Tahsilat360
                                            </h5>
                                            <small className="opacity-75 fw-medium">
                                                Türkiye'nin güvenilir ödeme platformu
                                            </small>
                                        </div>
                                        <div className="text-white text-center text-md-end">
                                            <div className="d-flex align-items-center justify-content-center justify-content-md-end gap-3 mb-2">
                                                <div className="d-flex align-items-center">
                                                    <i className="bx bx-shield-check me-1"></i>
                                                    <small className="opacity-75 fw-medium">256-bit SSL</small>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <i className="bx bx-check-circle me-1"></i>
                                                    <small className="opacity-75 fw-medium">PCI DSS</small>
                                                </div>
                                            </div>
                                            <small className="opacity-75">
                                                © {new Date().getFullYear()} Tahsilat360 | Online Tahsilat Sistemi. Tüm hakları saklıdır.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
            
        </div>
    );
};

export default CompanyWelcomePage;